import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchTransactions,
  checkTransactionStatus,
  sendCrypto,
  withdraw,
  updateWithdrawStatus,
  fetchDepositTransactions,
  fetchWithdrawalTransactions,
  processWithdrawal,
  speedUpWithdrawal,
  cancelWithdrawal,
  fetchTransactionById,
  createWithdrawal,
  fetchWithdrawalMonitor,
  fetchDepositMonitor,
  requeueWithdrawalTransaction
} from '../api';

/**
 * Hook for fetching transactions with pagination and filtering
 * @param filters Filter criteria
 * @param limit Maximum number of transactions to return
 * @param skip Number of transactions to skip (for pagination)
 * @param refreshInterval Interval in milliseconds to refresh data (default: 60000)
 * @returns Query result with transactions
 */
export const useTransactions = (
  filters: any = {},
  limit: number = 50,
  skip: number = 0,
  refreshInterval = 60000
) => {
  return useQuery({
    queryKey: ['transactions', filters, limit, skip],
    queryFn: () => fetchTransactions(filters, limit, skip),
    refetchInterval: refreshInterval
  });
};

/**
 * Hook for checking transaction status
 * @param txid Transaction ID
 * @param network Network name
 * @param is_evm Whether the network is EVM-compatible
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with transaction status
 */
export const useTransactionStatus = (
  txid: string,
  network: string,
  is_evm: number,
  refreshInterval = 30000
) => {
  return useQuery({
    queryKey: ['transactionStatus', txid, network, is_evm],
    queryFn: () => checkTransactionStatus(txid, network, is_evm),
    refetchInterval: refreshInterval,
    enabled: !!txid && !!network
  });
};

/**
 * Hook for sending crypto
 * @returns Mutation for sending crypto
 */
export const useSendCrypto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      provider: string;
      token: string;
      amount: string;
      to: string;
      privatekey: string;
      from?: string;
      is_evm: number;
      tag?: string;
      publicKey?: string;
      speed?: 'slow' | 'medium' | 'fast';
    }) => {
      const response = await sendCrypto(data);

      // Check if the response indicates an error
      if (response && response.status === false) {
        console.error('Transaction error:', response.error || response.data);
        // Return the response as is, so we can handle the error in the component
        return response;
      }

      // If successful, return the response
      return response;
    },
    onSuccess: (data) => {
      // Only invalidate queries if the transaction was actually successful
      if (data && data.status === true) {
        queryClient.invalidateQueries({ queryKey: ['transactions'] });
      }
    }
  });
};

/**
 * Hook for creating a withdrawal
 * @returns Mutation for creating a withdrawal
 */
export const useWithdraw = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      userid: number;
      amount: number;
      to: string;
      coin: string;
      network: string;
      is_evm: number;
      tag?: string;
    }) => withdraw(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['withdrawalTransactions'] });
    }
  });
};

/**
 * Hook for updating withdrawal status
 * @returns Mutation for updating withdrawal status
 */
export const useUpdateWithdrawStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      txid: string;
      status: string;
      networkresult?: string;
    }) => updateWithdrawStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['withdrawalTransactions'] });
    }
  });
};

/**
 * Hook for fetching deposit transactions
 * @param filters Filter criteria
 * @param limit Maximum number of transactions to return
 * @param skip Number of transactions to skip (for pagination)
 * @param refreshInterval Interval in milliseconds to refresh data (default: 60000)
 * @returns Query result with deposit transactions
 */
export const useDepositTransactions = (
  filters: any = {},
  limit: number = 50,
  skip: number = 0,
  refreshInterval = 60000
) => {
  return useQuery({
    queryKey: ['depositTransactions', filters, limit, skip],
    queryFn: () => fetchDepositTransactions(filters, limit, skip),
    refetchInterval: refreshInterval
  });
};

/**
 * Hook for fetching withdrawal transactions
 * @param filters Filter criteria
 * @param limit Maximum number of transactions to return
 * @param skip Number of transactions to skip (for pagination)
 * @param refreshInterval Interval in milliseconds to refresh data (default: 60000)
 * @returns Query result with withdrawal transactions
 */
export const useWithdrawalTransactions = (
  filters: any = {},
  limit: number = 50,
  skip: number = 0,
  refreshInterval = 60000
) => {
  return useQuery({
    queryKey: ['withdrawalTransactions', filters, limit, skip],
    queryFn: () => fetchWithdrawalTransactions(filters, limit, skip),
    refetchInterval: refreshInterval
  });
};

/**
 * Hook for processing a withdrawal transaction
 * @returns Mutation for processing a withdrawal
 */
export const useProcessWithdrawal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => processWithdrawal(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['withdrawalTransactions'] });
    }
  });
};

/**
 * Hook for speeding up a withdrawal transaction
 * @returns Mutation for speeding up a withdrawal
 */
export const useSpeedUpWithdrawal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, gasPriceMultiplier }: { id: string, gasPriceMultiplier: number }) =>
      speedUpWithdrawal(id, gasPriceMultiplier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['withdrawalTransactions'] });
    }
  });
};

/**
 * Hook for cancelling a withdrawal transaction
 * @returns Mutation for cancelling a withdrawal
 */
export const useCancelWithdrawal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, gasPriceMultiplier }: { id: string, gasPriceMultiplier: number }) =>
      cancelWithdrawal(id, gasPriceMultiplier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['withdrawalTransactions'] });
    }
  });
};

/**
 * Hook for fetching transaction details by ID
 * @param id Transaction ID
 * @returns Query result with transaction details
 */
export const useTransactionDetails = (id: string) => {
  return useQuery({
    queryKey: ['transactionDetails', id],
    queryFn: () => fetchTransactionById(id),
    enabled: !!id, // Only run the query if id is provided
    retry: 2
  });
};

/**
 * Hook for creating a withdrawal using the alternative API
 * This API doesn't require a private key as it can retrieve it from the database
 * @returns Mutation for creating a withdrawal
 */
export const useCreateWithdrawal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      from_address?: string;
      to: string;
      amount: string;
      coin: string;
      network: string;
      user_id: number;
      privateKey?: string;
      tag?: string;
      contract_address?: string;
      priority?: 'slow' | 'medium' | 'fast';
    }) => {
      const response = await createWithdrawal(data);

      // Check if the response indicates an error
      if (response && response.status === false) {
        console.error('Withdrawal error:', response.error || response.data);
        // Return the response as is, so we can handle the error in the component
        return response;
      }

      // If successful, return the response
      return response;
    },
    onSuccess: (data) => {
      // Only invalidate queries if the withdrawal was actually successful
      if (data && data.status === true) {
        queryClient.invalidateQueries({ queryKey: ['transactions'] });
        queryClient.invalidateQueries({ queryKey: ['withdrawalTransactions'] });
      }
    }
  });
};

/**
 * Hook for fetching withdrawal monitor data with detailed stage information
 * @param filters Filter criteria
 * @param limit Maximum number of withdrawals to return
 * @param skip Number of withdrawals to skip (for pagination)
 * @param sort Sort configuration
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with withdrawal monitor data
 */
export const useWithdrawalMonitor = (
  filters: any = {},
  limit: number = 50,
  skip: number = 0,
  sort?: { field: string; order: 'asc' | 'desc' },
  refreshInterval = 30000
) => {
  return useQuery({
    queryKey: ['withdrawalMonitor', filters, limit, skip, sort],
    queryFn: () => fetchWithdrawalMonitor(filters, limit, skip, sort),
    refetchInterval: refreshInterval,
    retry: 1,
    staleTime: 10000
  });
};

/**
 * Hook for fetching deposit monitor data with detailed stage information
 * @param filters Filter criteria
 * @param limit Maximum number of deposits to return
 * @param skip Number of deposits to skip (for pagination)
 * @param sort Sort configuration
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with deposit monitor data
 */
export const useDepositMonitor = (
  filters: any = {},
  limit: number = 50,
  skip: number = 0,
  sort?: { field: string; order: 'asc' | 'desc' },
  refreshInterval = 30000
) => {
  return useQuery({
    queryKey: ['depositMonitor', filters, limit, skip, sort],
    queryFn: () => fetchDepositMonitor(filters, limit, skip, sort),
    refetchInterval: refreshInterval,
    retry: 1,
    staleTime: 10000
  });
};

/**
 * Hook for re-queuing a stuck withdrawal transaction
 * @returns Mutation for re-queuing a withdrawal
 */
export const useRequeueWithdrawal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, adminNotes }: { transactionId: string; adminNotes?: string }) =>
      requeueWithdrawalTransaction(transactionId, adminNotes),
    onSuccess: () => {
      // Invalidate all withdrawal-related queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['withdrawalMonitor'] });
      queryClient.invalidateQueries({ queryKey: ['withdrawalTransactions'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    }
  });
};
